import { useState } from 'react';
import { sameItems, sameRows, sameTagFilters } from '@/hooks/selectEqual';
import { useThumbnailUrl } from '@/hooks/usePhotoSources';
import { useRowboat, useSelect } from '@/rowboat';
import { listItemGroups } from '@/services/itemGroupService';
import { aliasesOf, itemsByEstate } from '@/services/itemService';
import { primaryPhotoFor, readMedia, thumbHash } from '@/services/photoService';
import { type TagFilter, tagFilters } from '@/services/tagFilters';
import { Alert, Page, ViewOnlyNote } from './ui';

type ItemRow = ReturnType<typeof itemsByEstate>[number];

/** Held by the parent so the filters survive opening an item and coming back. */
export type ItemFilters = { query: string; itemGroupId: string; tag: string };

export const NO_FILTERS: ItemFilters = { query: '', itemGroupId: '', tag: '' };

const selectClass =
  'min-h-11 w-full rounded-xl border border-divider bg-surface-primary px-3 py-2.5 text-base text-content-primary';

export function ItemList({
  estateId,
  filters,
  onFilters,
  readOnly = false,
  onOpen,
  adding = false,
  onAdd,
}: {
  estateId: string;
  filters: ItemFilters;
  onFilters: (next: ItemFilters) => void;
  readOnly?: boolean;
  onOpen: (id: string) => void;
  adding?: boolean;
  onAdd: () => Promise<void>;
}) {
  const graph = useRowboat();
  const [error, setError] = useState('');
  const add = () => {
    setError('');
    onAdd().catch((err) => {
      console.error('add item failed', err);
      setError('Could not add the item');
    });
  };
  const items = useSelect(() => itemsByEstate(graph, estateId), sameItems);
  const itemGroups = useSelect(() => listItemGroups(graph, estateId), sameRows);
  const tags = useSelect(() => tagFilters(graph, estateId), sameTagFilters);
  const set = (patch: Partial<ItemFilters>) => onFilters({ ...filters, ...patch });
  // A tag whose last item was untagged drops out of the list, and the filter with it.
  const tag = tags.find((t) => t.name === filters.tag);

  const q = filters.query.trim().toLowerCase();
  const shown = items.filter(
    (item) =>
      (!filters.itemGroupId || item.item_group_id === filters.itemGroupId) &&
      (!tag || tag.itemIds.has(item.id)) &&
      (!q ||
        item.name.toLowerCase().includes(q) ||
        (item.description ?? '').toLowerCase().includes(q) ||
        item.inventory_number.toLowerCase().includes(q) ||
        aliasesOf(item).some((alias) => alias.toLowerCase().includes(q))),
  );
  const itemGroupName = (id: string) => itemGroups.find((r) => r.id === id)?.name ?? '';

  return (
    <Page label="Item list">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-content-primary">Items</h1>
        {!readOnly && (
          <button
            type="button"
            aria-label="Add item"
            title="Add item"
            disabled={adding}
            onClick={add}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl bg-primary-600 px-4 text-xl leading-none text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-primary-600"
          >
            <span aria-hidden="true">+</span>
          </button>
        )}
      </div>
      {error && <Alert>{error}</Alert>}
      {readOnly && <ViewOnlyNote />}
      <input
        aria-label="Search"
        value={filters.query}
        onChange={(e) => set({ query: e.target.value })}
        placeholder="Search items"
        className="min-h-11 w-full rounded-xl border border-divider bg-surface-primary px-4 py-2.5 text-base text-content-primary placeholder:text-content-tertiary focus:border-primary-500 focus:outline-none"
      />
      <div className="grid grid-cols-2 gap-3">
        <select
          aria-label="Group filter"
          value={filters.itemGroupId}
          onChange={(e) => set({ itemGroupId: e.target.value })}
          className={selectClass}
        >
          <option value="">All groups</option>
          {itemGroups.map((itemGroup) => (
            <option key={itemGroup.id} value={itemGroup.id}>
              {itemGroup.name}
            </option>
          ))}
        </select>
        <select
          aria-label="Tag filter"
          value={tag?.name ?? ''}
          onChange={(e) => set({ tag: e.target.value })}
          className={selectClass}
        >
          <option value="">All tags</option>
          <TagOptions label="Flags" tags={tags.filter((t) => t.flag)} />
          <TagOptions label="Tags" tags={tags.filter((t) => !t.flag)} />
        </select>
      </div>
      <ul className="space-y-2">
        {shown.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            itemGroupName={itemGroupName(item.item_group_id)}
            onOpen={onOpen}
          />
        ))}
      </ul>
      {shown.length === 0 && (
        <p className="py-8 text-center text-sm text-content-tertiary">
          {readOnly ? 'No items yet.' : 'Nothing here yet — tap + to add your first item.'}
        </p>
      )}
    </Page>
  );
}

function TagOptions({ label, tags }: { label: string; tags: TagFilter[] }) {
  if (tags.length === 0) return null;
  return (
    <optgroup label={label}>
      {tags.map((t) => (
        <option key={t.name} value={t.name}>
          {t.label} ({t.itemIds.size})
        </option>
      ))}
    </optgroup>
  );
}

function ItemRow({
  item,
  itemGroupName,
  onOpen,
}: {
  item: ItemRow;
  itemGroupName: string;
  onOpen: (id: string) => void;
}) {
  const graph = useRowboat();
  const hash = useSelect(() => thumbHash(primaryPhotoFor(graph, item.id)));
  const stored = useSelect(
    () => readMedia(primaryPhotoFor(graph, item.id)?.storage_key) !== undefined,
  );
  const thumbUrl = useThumbnailUrl(hash);
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(item.id)}
        className="flex w-full items-center gap-3 rounded-2xl border border-divider bg-surface-elevated p-3 text-left shadow-sm transition active:scale-[0.99] hover:bg-interactive-hover"
      >
        {thumbUrl ? (
          <img src={thumbUrl} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
        ) : (
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-surface-tertiary text-xs text-content-tertiary">
            {stored ? 'No preview' : 'No photo'}
          </span>
        )}
        <span className="min-w-0">
          <span className="block truncate text-base font-medium text-content-primary">
            {item.name || item.inventory_number}
          </span>
          <span className="block text-sm text-content-secondary">
            {item.inventory_number} · {itemGroupName}
          </span>
        </span>
      </button>
    </li>
  );
}
